import axios from 'axios';

// Match the project-wide env var (`REACT_APP_API_URL`) used by every other
// service. The previous `REACT_APP_API_BASE_URL` name doesn't exist in any
// deployment, so production fell through to the localhost fallback —
// causing every analysis request to call http://localhost:5000/api/...
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// =============================================
// EXAM-BASED ANALYSIS INTERFACES
// =============================================

export interface ExamDataPoint {
  examId: string;
  examName: string;
  examDate: string;
  percentage: number;
  obtainedMarks: number;
  totalMarks: number;
  grade?: string;
  isAbsent?: boolean;
}

export interface SubjectExamData {
  subjectId: string;
  subjectName: string;
  examData: ExamDataPoint[];
}

export interface StudentAnalysisResponse {
  student: {
    _id: string;
    name: string;
    rollNumber: string;
  };
  session: {
    _id: string;
    name: string;
  };
  center: {
    _id: string;
    name: string;
  };
  subjects: SubjectExamData[];
  overallTrend: ExamDataPoint[];
}

export interface SessionSubjectExamData {
  subjectId: string;
  subjectName: string;
  examData: {
    examId: string;
    examName: string;
    examDate: string;
    averagePercentage: number;
    totalStudents: number;
    maxPercentage: number;
    minPercentage: number;
  }[];
}

export interface SessionAnalysisResponse {
  session: {
    _id: string;
    name: string;
  };
  subjects: SessionSubjectExamData[];
  /** Per exam, the class average across all subjects: the same figure Class Performance shows. */
  overallTrend: SessionSubjectExamData['examData'];
}

export interface ExamClassPerformanceResponse {
  session: {
    _id: string;
    name: string;
  };
  class: {
    _id: string;
    name: string;
  };
  exam: {
    _id: string;
    name: string;
    startDate: string;
  };
  subject: {
    _id: string;
    name: string;
  };
  /** Mean of the students' percentages; null when nobody has a score. */
  classAverage: number | null;
  /** Whether the school has pass rules on (Settings → Results); when off, every outcome is null. */
  passRules: boolean;
  students: {
    _id: string;
    name: string;
    rollNumber: string;
    percentage: number;
    obtainedMarks: number;
    totalMarks: number;
    grade: string;
    /** Competition rank by percentage: ties share a rank. */
    rank: number;
    outcome: 'PASS' | 'FAIL' | 'PENDING' | null;
    /** Papers failed in this exam (all-subjects view only). */
    failedSubjectIds: string[];
  }[];
}

export interface ExamFilterItem {
  _id: string;
  name: string;
  sessionId: string | null;
  classIds: string[];
  /** The subjects this exam sets papers in. */
  subjectIds: string[];
  startDate: string;
}

export interface SessionFilterItem {
  _id: string;
  name: string;
  status?: 'planning' | 'active' | 'completed' | 'archived';
  startDate?: string;
  endDate?: string;
  centerId: {
    _id: string;
    name: string;
  };
  activeClassId?: string | null;
}

export interface AnalysisStudentItem {
  _id: string;
  name: string;
  rollNumber: string;
  // Enrollment-derived scoping fields (FE narrows the dropdown by these).
  sessionId?: string | null;
  classId?: string | null;
  sectionInstanceId?: string | null;
  centerId: {
    _id: string;
    name: string;
  };
}

export interface AnalysisFilterOptions {
  centers: {
    _id: string;
    name: string;
  }[];
  sessions: SessionFilterItem[];
  classes: {
    _id: string;
    name: string;
    centerId: {
      _id: string;
      name: string;
    };
  }[];
  subjects: {
    _id: string;
    name: string;
    centerId: {
      _id: string;
      name: string;
    };
  }[];
  students: AnalysisStudentItem[];
  exams: ExamFilterItem[];
}

export interface AnalysisFilters {
  studentId?: string;
  classId?: string;
  sessionId?: string;
  centerId?: string;
  subjectIds?: string[];
  examCount?: number;
}

export interface ExamClassPerformanceFilters {
  sessionId: string;
  classId: string;
  examId: string;
  subjectId: string;
  centerId?: string;
}

// =============================================
// API FUNCTIONS
// =============================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export const getAnalysisFilters = async (): Promise<AnalysisFilterOptions> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/analysis/filters`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching analysis filters:', error);
    throw error;
  }
};

export const getStudentAnalysis = async (
  filters: AnalysisFilters
): Promise<StudentAnalysisResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.studentId) params.append('studentId', filters.studentId);
    if (filters.classId) params.append('classId', filters.classId);
    if (filters.sessionId) params.append('sessionId', filters.sessionId);
    if (filters.centerId) params.append('centerId', filters.centerId);
    if (filters.examCount) params.append('examCount', filters.examCount.toString());

    if (filters.subjectIds && filters.subjectIds.length > 0 && !filters.subjectIds.includes('all')) {
      filters.subjectIds.forEach(id => params.append('subjectIds', id));
    }

    const response = await axios.get(
      `${API_BASE_URL}/analysis/student?${params.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching student analysis:', error);
    throw error;
  }
};

// Endpoint path kept as `/analysis/batch` to avoid a backend-route rename, but
// the underlying contract is now session-scoped.
export const getSessionAnalysis = async (
  filters: AnalysisFilters
): Promise<SessionAnalysisResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.classId) params.append('classId', filters.classId);
    if (filters.sessionId) params.append('sessionId', filters.sessionId);
    if (filters.centerId) params.append('centerId', filters.centerId);
    if (filters.examCount) params.append('examCount', filters.examCount.toString());

    if (filters.subjectIds && filters.subjectIds.length > 0 && !filters.subjectIds.includes('all')) {
      filters.subjectIds.forEach(id => params.append('subjectIds', id));
    }

    const response = await axios.get(
      `${API_BASE_URL}/analysis/batch?${params.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching session analysis:', error);
    throw error;
  }
};

export const getExamClassPerformance = async (
  filters: ExamClassPerformanceFilters
): Promise<ExamClassPerformanceResponse> => {
  try {
    const params = new URLSearchParams();

    params.append('sessionId', filters.sessionId);
    params.append('classId', filters.classId);
    params.append('examId', filters.examId);
    params.append('subjectId', filters.subjectId);

    if (filters.centerId) params.append('centerId', filters.centerId);

    const response = await axios.get(
      `${API_BASE_URL}/analysis/exam-class-performance?${params.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching exam class performance:', error);
    throw error;
  }
};

// =============================================
// UTILITY FUNCTIONS
// =============================================

export const generateChartColors = (count: number): string[] => {
  // Premium color palette - elegant, vibrant, works in both light and dark themes
  const colors = [
    '#818cf8', // Soft Indigo - primary subject color
    '#34d399', // Bright Emerald - success/growth
    '#fbbf24', // Golden Amber - attention
    '#f472b6', // Soft Pink - distinct
    '#22d3ee', // Bright Cyan - fresh
    '#a78bfa', // Soft Violet - secondary
    '#fb923c', // Warm Orange - energy
    '#4ade80', // Bright Green - positive
    '#f87171', // Soft Red - warning
    '#38bdf8', // Sky Blue - calm
    '#c084fc', // Purple - creative
    '#facc15', // Yellow - highlight
  ];

  if (count > colors.length) {
    for (let i = colors.length; i < count; i++) {
      const hue = (i * 137.508) % 360;
      colors.push(`hsl(${hue}, 65%, 60%)`);
    }
  }

  return colors.slice(0, count);
};
